import { Entity, Quat, Vec3 } from "playcanvas";
import {
	AttacherInstance,
	AttacherNode,
	AttacherNodesByModelId,
	AttachmentSolution,
} from "@animech/supermodel";
import { AttacherState, ConstAttacherState } from "./attacher-state";
import { Model } from "@definitions";

export type EntitySeparationMap = WeakMap<Entity, number>;

/**
 * Creates a map of all entities in an attacher state that have some sort of
 * attachment to a list of "separator" entities. The calculated "separation"
 * is a number representing the least amount of separating entities in a chain.
 * The absence of an entity denotes that it is entirely separated.
 */
export function createEntityAttachmentSeparationMap(
	attacherState: ConstAttacherState,
	separators: Entity[],
): EntitySeparationMap {
	const result: EntitySeparationMap = new WeakMap();
	const seenEntities = new Set<Entity>();
	const pendingEntities = new Set(separators);

	let separation = -1;

	while (pendingEntities.size > 0) {
		const entities = [...pendingEntities];
		pendingEntities.clear();

		for (const entity of entities) {
			if (seenEntities.has(entity)) {
				continue;
			}
			seenEntities.add(entity);
			if (!separators.includes(entity)) {
				result.set(entity, separation);
			}
			const attachers = attacherState.getByEntity(entity) ?? [];

			for (const attacher of attachers) {
				for (const attachment of attacher.attachments) {
					const attachmentEntity = attachment.entity;
					if (!seenEntities.has(attachmentEntity)) {
						pendingEntities.add(attachmentEntity);
					}
				}
			}
		}
		separation++;
	}

	return result;
}

/**
 * Checks potentially copied attachers for a common origin.
 */
export function compareAttachers(
	lhs: AttacherInstance,
	rhs: AttacherInstance,
): boolean {
	// The node object and entity references should uniquely identify an attacher
	// and remain unchanged in copies.
	return lhs.node === rhs.node && lhs.entity === rhs.entity;
}

export function getAttachedEntities(
	entityGroup: Entity[],
	attacherState: AttacherState,
	attachmentFilter: (from: AttacherInstance, to: AttacherInstance) => boolean,
): Entity[] {
	const result: ReturnType<typeof getAttachedEntities> = [];

	const processEntity = (entity: Entity) => {
		const attacherInstances = attacherState.getByEntity(entity) ?? [];

		for (const attacherInstance of attacherInstances) {
			for (const attachment of attacherInstance.attachments) {
				const attachmentEntity = attachment.entity;

				if (
					!attachmentFilter(attacherInstance, attachment) ||
					result.includes(attachmentEntity) ||
					entityGroup.includes(attachmentEntity)
				) {
					continue;
				}

				result.push(attachmentEntity);
				processEntity(attachmentEntity);
			}
		}
	};

	for (const entity of entityGroup) {
		processEntity(entity);
	}

	return result;
}

export function createDefaultAttachmentSolution(
	entities: Entity[],
): AttachmentSolution {
	return {
		entityTransforms: entities.map((entity) => {
			return {
				entity,
				position: Vec3.ZERO.clone(),
				rotation: Quat.IDENTITY.clone(),
				deltaPosition: Vec3.ZERO.clone(),
				deltaRotation: Quat.IDENTITY.clone(),
				instances: [],
			};
		}),
		instances: [],
	};
}

export function synchronizeAttachmentSolution(
	attachmentSolution: AttachmentSolution,
	attacherState: ConstAttacherState,
): void {
	attachmentSolution.instances = [];

	for (const entityTransform of attachmentSolution.entityTransforms) {
		entityTransform.position.copy(entityTransform.entity.getPosition());
		entityTransform.rotation.copy(entityTransform.entity.getRotation());
		entityTransform.instances = (attacherState.getByEntity(
			entityTransform.entity,
		) ?? []) as AttacherInstance[];
		attachmentSolution.instances.push(...entityTransform.instances);
	}
}

export function detachEntityGroup(
	entityGroup: Entity[],
	attacherInstances: AttacherInstance[],
	groupAttachersOut?: AttacherInstance[],
	otherAttachersOut?: AttacherInstance[],
): void {
	for (const attacherInstance of attacherInstances) {
		const instanceIsInternal = entityGroup.includes(attacherInstance.entity);
		const { attachments } = attacherInstance;

		for (
			let attachmentIndex = attachments.length - 1;
			attachmentIndex >= 0;
			--attachmentIndex
		) {
			const attachment = attacherInstance.attachments[attachmentIndex];
			const attachmentIsInternal = entityGroup.includes(attachment.entity);

			if (instanceIsInternal !== attachmentIsInternal) {
				attachments.splice(attachmentIndex, 1);
			}
		}

		(instanceIsInternal ? groupAttachersOut : otherAttachersOut)?.push(
			attacherInstance,
		);
	}
}

export function getPartialVectorLength(
	vector: Vec3,
	x: boolean,
	y: boolean,
	z: boolean,
) {
	return Math.sqrt(
		(x ? Math.pow(vector.x, 2) : 0) +
			(y ? Math.pow(vector.y, 2) : 0) +
			(z ? Math.pow(vector.z, 2) : 0),
	);
}

type ModelWithSuffix = `${Model}.glb`;

export function withGlbSuffix(modelNames: readonly Model[]): ModelWithSuffix[] {
	return modelNames.map((modelName) => `${modelName}.glb` as ModelWithSuffix);
}

export function withoutGlbSuffix(modelId: string): Model {
	const suffixIndex = modelId.lastIndexOf(".glb");
	return suffixIndex > -1
		? (modelId.slice(0, suffixIndex) as Model)
		: (modelId as Model);
}

/**
 * Creates a Map-version of `AttacherNodesByModelId`, where
 * all keys have been stripped of their glb extensions.
 */
export function createModelNodesMap(
	attacherNodesByModel: AttacherNodesByModelId,
): Map<Model, AttacherNode[]> {
	return new Map(
		Object.entries(attacherNodesByModel).map(
			([modelFilename, nodes]) =>
				[withoutGlbSuffix(modelFilename), nodes] as const,
		),
	);
}

/**
 * Creates a "lookup" for finding the model
 * associated with an attacher node.
 */
export function createNodeModelMap(
	modelNodesMap: Map<Model, AttacherNode[]>,
): Map<AttacherNode, Model> {
	const nodeModelMap = new Map<AttacherNode, Model>();
	for (const [modelId, nodes] of modelNodesMap) {
		for (const node of nodes) {
			nodeModelMap.set(node, modelId);
		}
	}
	return nodeModelMap;
}
